import { use } from 'react';

import { SelectedRoleContextObj } from '../context';
import { type NotPresentSelectedRoleContext, type SelectedRoleContext } from '../types';

interface UseSelectedRoleOptions {
  readonly strict?: boolean;
}

type UseSelectedRoleContext<O extends UseSelectedRoleOptions> = O extends {
  readonly strict: false;
}
  ? NotPresentSelectedRoleContext | SelectedRoleContext
  : SelectedRoleContext;

/**
 * A hook that allows the {@link SelectedRoleContext} to be accessed in any component.
 *
 * @see AccessTypeFlags
 *
 * @throws {Error}
 *   An error if the selected role context, {@link SelectedRoleContext}, is not in scope.
 *
 * @returns {SelectedRoleContext}
 *   Contextual information about the user's current {@link AppRole}, their current
 *   {@link Organization} (notably, their current organization type, {@link OrganizationType}),
 *   information about their current access types, {@link AccessType}, and additional context
 *   derived from this information.
 */
export const useSelectedRole = <O extends UseSelectedRoleOptions>(
  options?: O,
): UseSelectedRoleContext<O> => {
  const context = use(SelectedRoleContextObj);
  if (!context) {
    if (options?.strict === false) {
      return {} as UseSelectedRoleContext<O>;
    }
    throw new Error('The "useSelectedRole" hook must be used inside of the SelectedRoleProvider"!');
  }
  return context;
};
